// src/layouts/components/dashboard/DashboardProfileSection.jsx

import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/authContext";
import AVIcon from "../../../components/icons/AllVoicedIcons";
import countryOptions from "../../../constants/countryOptions";
import languageOptions from "../../../constants/languageOptions";
import {
  ApplicationButton,
  ApplicationContextualHelp,
  ApplicationSectionHeader,
  ApplicationStateRegion,
  ApplicationSurface
} from "../../../components/application";

const PROFILE_ROUTE = "/app/profile";

const VALUE_LABELS = {
  SLP: "Speech-language pathologist",
  ENT: "ENT / otolaryngologist",
  other_medical_doctor: "Other medical doctor",
  vocal_coach: "Vocal coach",
  singing_teacher: "Singing teacher",
  singer: "Singer",
  musician: "Musician",
  engineer: "Engineer",
  technologist: "Technologist",
  linguist: "Linguist",
  other: "Other",

  speech_language_pathology: "Speech-language pathology",
  medicine: "Medicine",
  otolaryngology: "Otolaryngology",
  music: "Music",
  vocal_pedagogy: "Vocal pedagogy",
  engineering: "Engineering",
  linguistics: "Linguistics",
  neuroscience: "Neuroscience",
  education: "Education",
  performing_arts: "Performing arts",
  technology: "Technology",
  research: "Research",

  student: "Student",
  teacher: "Teacher",
  researcher: "Researcher",
  clinician: "Clinician",
  artist: "Artist",
  developer: "Developer",

  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
  expert: "Expert",

  learning: "Learning",
  teaching: "Teaching",
  clinical: "Clinical",

  no_known_condition: "No known hearing condition",
  mild_hearing_loss: "Mild hearing loss",
  moderate_severe_hearing_loss: "Moderate/severe hearing loss",
  tinnitus: "Tinnitus",
  hearing_aid_or_ci: "Hearing aid or cochlear implant",
  prefer_not_to_say: "Prefer not to say"
};

const LANGUAGE_LABELS = Object.fromEntries(
  languageOptions.map((language) => [
    language.value,
    language.label
  ])
);

const COUNTRY_LABELS = Object.fromEntries(
  countryOptions.map((country) => [
    country.value,
    country.label
  ])
);

function hasValue(value) {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return (
    value !== undefined &&
    value !== null &&
    value !== ""
  );
}

function formatSingleValue(value, valueType) {
  if (!hasValue(value)) {
    return "";
  }

  if (valueType === "language") {
    return (
      LANGUAGE_LABELS[value] ||
      value
    );
  }

  if (valueType === "country") {
    const countryCode =
      typeof value === "string"
        ? value.toUpperCase()
        : value;

    return (
      COUNTRY_LABELS[countryCode] ||
      value
    );
  }

  return (
    VALUE_LABELS[value] ||
    value
      .toString()
      .replaceAll("_", " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
  );
}

function formatValue(value, valueType) {
  if (Array.isArray(value)) {
    return value
      .map((item) => formatSingleValue(item, valueType))
      .filter(Boolean)
      .join(", ");
  }

  return formatSingleValue(value, valueType);
}

export default function DashboardProfileSection() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const profileItems = [
    {
      key: "profession",
      label: "Profession",
      value: user?.profession,
      icon: "user"
    },
    {
      key: "professional-roles",
      label: "Roles",
      value: user?.professionalRoles,
      icon: "team"
    },
    {
      key: "voice-competence-level",
      label: "Voice competence",
      value: user?.voiceCompetenceLevel,
      icon: "trophy"
    },
    {
      key: "country-of-residence",
      label: "Country",
      value: user?.countryOfResidence,
      valueType: "country",
      icon: "globe"
    },
    {
      key: "communication-language",
      label: "Communication language",
      value: user?.communicationLanguage,
      valueType: "language",
      icon: "languages"
    },
    {
      key: "main-use",
      label: "Main use",
      value: user?.mainUse,
      icon: "target"
    },
    {
      key: "hearing-context",
      label: "Hearing context",
      value: user?.hearingContext,
      icon: "listen"
    }
  ];

  const completedItems =
    profileItems.filter((item) => hasValue(item.value));

  const hasProfileInformation =
    completedItems.length > 0;

  const isProfileSubstantiallyComplete =
    completedItems.length >= 5;

  const missingItems =
    profileItems.length - completedItems.length;

  return (
    <section
      aria-labelledby="dashboard-profile-title"
      className="av-dashboard-section av-dashboard-profile"
    >
      {hasProfileInformation ? (
        <>
          <ApplicationSectionHeader
            title={(
              <span className="av-app-section-header__title-row">
                <span>Evaluator profile</span>
                <ApplicationContextualHelp
                  label="About your evaluator profile"
                  title="About your evaluator profile"
                  learnMoreLabel="Manage your profile"
                  learnMoreTo="/app/profile"
                >
                  <p>
                    Your evaluator profile provides context about you as a
                    listener and evaluator, such as your professional
                    background, voice competence, language, and hearing
                    context. This helps All-Voiced personalize your experience
                    and interpret perceptual data with appropriate evaluator
                    context.
                  </p>
                </ApplicationContextualHelp>
              </span>
            )}
            titleId="dashboard-profile-title"
            description="Your profile helps personalize activities and supports structured perceptual data generation."
            actions={
              <ApplicationButton
                variant="secondary"
                onClick={() => navigate(PROFILE_ROUTE)}
              >
                View profile
              </ApplicationButton>
            }
          />

          <ApplicationSurface
            as="div"
            className="av-dashboard-profile__summary"
            variant="subtle"
          >
            {profileItems.map((item) => (
              <div
                key={item.key}
                className={[
                  "av-dashboard-profile__item",
                  hasValue(item.value) ? "" : "is-missing"
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <div className="av-dashboard-profile__icon" aria-hidden="true">
                  <AVIcon
                    icon={hasValue(item.value) ? item.icon : "info"}
                    size={18}
                  />
                </div>

                <div>
                  <span>{item.label}</span>

                  <strong>
                    {hasValue(item.value)
                      ? formatValue(item.value, item.valueType)
                      : "Not provided yet"}
                  </strong>
                </div>
              </div>
            ))}
          </ApplicationSurface>

          {!isProfileSubstantiallyComplete && (
            <ApplicationStateRegion
              message={
                <>
                  {missingItems === 1
                    ? "One profile field is still missing."
                    : `${missingItems} profile fields are still missing.`}{" "}
                  Add the missing information to improve how All-Voiced
                  contextualizes your activities, training, and
                  evaluator-related data.
                </>
              }
              status="information"
            />
          )}
        </>
      ) : (
        <>
          <ApplicationSectionHeader
            title={(
              <span className="av-app-section-header__title-row">
                <span>Evaluator profile</span>
                <ApplicationContextualHelp
                  label="About your evaluator profile"
                  title="About your evaluator profile"
                  learnMoreLabel="Manage your profile"
                  learnMoreTo="/app/profile"
                >
                  <p>
                    Your evaluator profile provides context about you as a
                    listener and evaluator, such as your professional
                    background, voice competence, language, and hearing
                    context. This helps All-Voiced personalize your experience
                    and interpret perceptual data with appropriate evaluator
                    context.
                  </p>
                </ApplicationContextualHelp>
              </span>
            )}
            titleId="dashboard-profile-title"
          />
          <ApplicationSurface
            as="div"
            className="av-dashboard-empty"
            variant="subtle"
          >
            <div
              className="av-app-icon-badge av-dashboard-empty__icon"
              aria-hidden="true"
            >
              <AVIcon
                icon="user"
                size={24}
              />
            </div>

            <div>
              <h3>Complete your evaluator profile</h3>

              <p>
                Your profile helps All-Voiced understand your
                professional background, language context, intended use,
                voice competence level, and listening conditions. This
                supports more meaningful training, research participation,
                and structured perceptual data.
              </p>

              <ApplicationButton
                onClick={() => navigate(PROFILE_ROUTE)}
              >
                Complete profile
              </ApplicationButton>
            </div>
          </ApplicationSurface>
        </>
      )}
    </section>
  );
}
